import {Environment, graphql} from 'react-relay';
import {MutationConfig, RecordSourceSelectorProxy} from 'relay-runtime';

import Util from '../../forecast-app/shared/util/util';
import {trackAndCommitMutation} from '../../tracking/amplitude/TrackMutation';
import {AssignPersonToTaskInput, AssignPersonToTaskMutation} from './__generated__/AssignPersonToTaskMutation.graphql';
import {MutationInput, RelayOnCompleted, RelayOnError} from './MutationTypes';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const handleAssignPersonToTaskErrors = (response): boolean => {
	const errors = response.assignPersonToTask?.errors;
	if (errors && errors.length === 1) {
		return Util.checkForSageErrorAndShowModal(errors, false);
	}
	return false;
};

export const taskAssignmentFragment = graphql`
	fragment AssignPersonToTaskMutation_task on Task {
		id
		assignedPersons {
			id
		}
		parentTaskId
		parentTask {
			id
		}
		billable
		blocked
		bug
		companyTaskId
		deadlineDay
		deadlineMonth
		deadlineYear
		deadlineFrom
		startFrom
		done
		highPriority
		id
		role {
			id
			name
		}
		phase {
			id
			progress
			progressDetails {
				progress
			}
		}
		sprint {
			id
		}
		statusColumnV2 {
			id
		}
		progress
		startDay
		startMonth
		startYear
		timeLeft
		timeLeftMinutesWithoutFutureTimeRegs
		hasChildren
		name
		progressDetails {
			progress
		}
		estimateForecast
		estimateForecastPrice
		estimateForecastMinutes
		approved
		currentPrice
		allTotalTimeAndExpensesAtCompletion
		plannedCost
		actualCost
		taskLabels {
			label {
				id
				name
				color
			}
		}
		project {
			id
			completion
			remaining
			forecast
			progress
			progressDetails {
				progress
			}
		}
		owner {
			id
		}
		followers {
			id
		}
		timeRegistrations {
			edges {
				node {
					billableMinutesRegistered
				}
			}
		}
	}
`;

const mutation = graphql`
	mutation AssignPersonToTaskMutation($input: AssignPersonToTaskInput!) {
		assignPersonToTask(input: $input) {
			errors
			task {
				...AssignPersonToTaskMutation_task @relay(mask: false)
			}
		}
	}
`;

// Imperative updater since assignedPersons does not follow relay connection paradigm. With a connection, RANGE_ADD would be usable instead.
// Alternatively could pass in full list of assignees and manipulate with standard optimistic updater, but feels more correct to allow mutation to run in isolation.
const optimisticUpdater = (input: MutationInput<AssignPersonToTaskInput>, store: RecordSourceSelectorProxy) => {
	if (!input.taskId || !input.personId) return;

	const taskNode = store.get(input.taskId);
	const existingAssignedPersons = taskNode?.getLinkedRecords('assignedPersons') ?? [];
	const newAssignedPerson = store.get(input.personId);
	if (newAssignedPerson) {
		const newAssignedPersons = [...existingAssignedPersons, newAssignedPerson];
		taskNode?.setLinkedRecords(newAssignedPersons, 'assignedPersons');
	}
};

function commit(
	environment: Environment,
	input: MutationInput<AssignPersonToTaskInput>,
	onSuccess: RelayOnCompleted<AssignPersonToTaskMutation>,
	onError: RelayOnError
): Disposable {
	const mutationConfig: MutationConfig<AssignPersonToTaskMutation> = {
		mutation,
		variables: {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}},
		optimisticUpdater: store => optimisticUpdater(input, store),
		onCompleted: onSuccess,
		onError: onError,
	};
	return trackAndCommitMutation(environment, mutationConfig);
}

export default {commit};
